import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  packagesCreate,
  packagesList,
  packagesRemove,
  packagesUpdate,
} from '@/api/generated/packages/packages';
import {
  patientPackagesCancel,
  patientPackagesGetById,
  patientPackagesList,
  patientPackagesRefund,
  patientPackagesSell,
} from '@/api/generated/patient-packages/patient-packages';
import { patientPaymentsCreate } from '@/api/generated/accounting-patient-payments/accounting-patient-payments';
import { clinicProceduresList } from '@/api/generated/clinic-procedures/clinic-procedures';
import type {
  CancelPatientPackageDto,
  ClinicProcedureResponse,
  CreatePackageDto,
  CreatePatientPaymentDto,
  CreatePatientPaymentResponse,
  PackageListResponse,
  PackageResponse,
  PackagesListParams,
  PatientPackageListResponse,
  PatientPackageResponse,
  PatientPackagesListParams,
  RefundPatientPackageDto,
  SellPackageDto,
  UpdatePackageDto,
} from '@/api/generated/model';
import { useAuth } from '@/auth/token-store';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE PACKAGES DATA LAYER
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Two different objects live behind one nav entry and it is worth being precise
 * about which is which, because confusing them is the bug this whole feature is
 * shaped to prevent:
 *
 *   **`Package`** is the clinic's OFFER — «۶ جلسه لیزر», a price, a validity
 *   window, a list of covered procedures. It is editable, and editing it is
 *   normal: prices go up.
 *
 *   **`PatientPackage`** is a SALE. It carries `snapshot`, a frozen copy of the
 *   package as it was at purchase, and the API returns the sold terms from that
 *   snapshot — `name`, `sessionsTotal`, `procedures[]` on a `PatientPackage`
 *   are the snapshot's, not the live row's. So the sale screens read
 *   `PatientPackageResponse` and never join back to `PackageResponse`, and a
 *   clinic raising its laser price does not retroactively change what a patient
 *   bought in خرداد.
 *
 * As in the inventory feature: orval's generated HOOKS for these tags have the
 * query/mutation split inverted (a GET arrives as `useMutation`, a POST as a
 * `useQuery` that fires on mount), so only its request FUNCTIONS are used and
 * the read/write shape is declared here.
 */

export const packageKeys = {
  root: (clinicId: string | null) => ['packages', clinicId] as const,
  bundles: (clinicId: string | null, params: PackagesListParams) =>
    ['packages', clinicId, 'bundles', params] as const,
  sales: (clinicId: string | null, params: PatientPackagesListParams) =>
    ['packages', clinicId, 'sales', params] as const,
  sale: (clinicId: string | null, id: string) => ['packages', clinicId, 'sale', id] as const,
  procedures: (clinicId: string | null) => ['packages', clinicId, 'procedures'] as const,
  sellable: (clinicId: string | null) => ['packages', clinicId, 'sellable'] as const,
} as const;

/* -------------------------------------------------------------------------- */
/* Reads                                                                      */
/* -------------------------------------------------------------------------- */

export function usePackages(
  params: PackagesListParams,
  enabled = true,
): UseQueryResult<PackageListResponse> {
  const { activeClinicId } = useAuth();
  return useQuery({
    queryKey: packageKeys.bundles(activeClinicId, params),
    // The previous page stays on screen under the table's own spinner while
    // the next one loads, instead of the table collapsing to empty and back.
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => packagesList(params, signal),
    enabled: enabled && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

export function usePatientPackages(
  params: PatientPackagesListParams,
  enabled = true,
): UseQueryResult<PatientPackageListResponse> {
  const { activeClinicId } = useAuth();
  return useQuery({
    queryKey: packageKeys.sales(activeClinicId, params),
    // The previous page stays on screen under the table's own spinner while
    // the next one loads, instead of the table collapsing to empty and back.
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => patientPackagesList(params, signal),
    enabled: enabled && Boolean(activeClinicId),
    staleTime: 30_000,
  });
}

/**
 * One sale, kept current while its drawer is open.
 *
 * The drawer opens on the row the list (or the sell response) handed it, so
 * that row is the initial data and nothing is fetched on open. After a
 * payment, a cancel or a refund the packages keys are invalidated and this
 * refetches — the balance a receptionist just settled does not keep reading
 * as owed until the drawer is closed and opened again.
 */
export function usePatientPackage(
  sale: PatientPackageResponse | null,
): UseQueryResult<PatientPackageResponse> {
  const { activeClinicId } = useAuth();
  const id = sale?.id ?? '';
  return useQuery({
    queryKey: packageKeys.sale(activeClinicId, id),
    queryFn: ({ signal }) => patientPackagesGetById(id, signal),
    initialData: sale ?? undefined,
    enabled: Boolean(activeClinicId) && sale !== null,
    staleTime: 30_000,
  });
}

/**
 * The clinic's enabled procedures — what a bundle may be built out of.
 *
 * Every page of them, not the first hundred: a service past the hundredth had
 * no option in the bundle form, and an edited bundle that used it showed the
 * raw procedure id in the select. One or two requests for any real clinic.
 */
export function useBundleProcedures(enabled = true): UseQueryResult<ClinicProcedureResponse[]> {
  const { activeClinicId } = useAuth();
  return useQuery({
    queryKey: packageKeys.procedures(activeClinicId),
    queryFn: async ({ signal }) => {
      const rows: ClinicProcedureResponse[] = [];
      for (let page = 1; page <= 20; page += 1) {
        const result = await clinicProceduresList({ page, pageSize: 100, isEnabled: true }, signal);
        rows.push(...result.items);
        if (page >= result.pageCount) break;
      }
      return rows;
    },
    enabled: enabled && Boolean(activeClinicId),
    staleTime: 5 * 60_000,
  });
}

/**
 * The bundles that can be sold today, for the sale drawer opened WITHOUT a
 * bundle — from the sales tab or a patient file, where the operator starts
 * from the patient rather than from a row of the bundles list.
 */
export function useSellableBundles(enabled = true): UseQueryResult<PackageResponse[]> {
  const { activeClinicId } = useAuth();
  return useQuery({
    queryKey: packageKeys.sellable(activeClinicId),
    queryFn: ({ signal }) =>
      packagesList({ isActive: true, pageSize: 100 }, signal).then((page) => page.items),
    enabled: enabled && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

/* -------------------------------------------------------------------------- */
/* Writes                                                                     */
/* -------------------------------------------------------------------------- */

function useInvalidatePackages(): () => void {
  const queryClient = useQueryClient();
  const { activeClinicId } = useAuth();
  return () => {
    void queryClient.invalidateQueries({ queryKey: packageKeys.root(activeClinicId) });
  };
}

export function useCreatePackage(): UseMutationResult<PackageResponse, unknown, CreatePackageDto> {
  const invalidate = useInvalidatePackages();
  return useMutation({
    mutationFn: (body: CreatePackageDto) => packagesCreate(body),
    onSuccess: invalidate,
  });
}

export function useUpdatePackage(): UseMutationResult<
  PackageResponse,
  unknown,
  { id: string; body: UpdatePackageDto }
> {
  const invalidate = useInvalidatePackages();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdatePackageDto }) => packagesUpdate(id, body),
    onSuccess: invalidate,
  });
}

/**
 * DELETES a bundle definition — `deletedAt`, gone from every list, with no
 * restore in the panel. Its name is free again afterwards: uniqueness is a
 * partial index over live bundles only (`Package_clinicId_name_live_key`), so
 * a clinic that deletes «۶ جلسه لیزر» can define a new one under the same name.
 *
 * This used to be the row's «بایگانی» button, which is why managers archived a
 * package and could never find it again. «بایگانی» is now `isActive: false`
 * through `useUpdatePackage` — listed under «بایگانی», one click from being
 * sold again — and this is offered only inside the editor, for a bundle that
 * was never sold. Sales already made are unaffected either way: they carry
 * their own snapshot.
 */
export function useRemovePackage(): UseMutationResult<void, unknown, string> {
  const invalidate = useInvalidatePackages();
  return useMutation({
    mutationFn: (id: string) => packagesRemove(id),
    onSuccess: invalidate,
  });
}

export function useSellPackage(): UseMutationResult<
  PatientPackageResponse,
  unknown,
  SellPackageDto
> {
  const invalidate = useInvalidatePackages();
  return useMutation({
    mutationFn: (body: SellPackageDto) => patientPackagesSell(body),
    onSuccess: invalidate,
  });
}

export function useCancelPatientPackage(): UseMutationResult<
  PatientPackageResponse,
  unknown,
  { id: string; body: CancelPatientPackageDto }
> {
  const invalidate = useInvalidatePackages();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CancelPatientPackageDto }) =>
      patientPackagesCancel(id, body),
    onSuccess: invalidate,
  });
}

export function useRefundPatientPackage(): UseMutationResult<
  PatientPackageResponse,
  unknown,
  { id: string; body: RefundPatientPackageDto }
> {
  const invalidate = useInvalidatePackages();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RefundPatientPackageDto }) =>
      patientPackagesRefund(id, body),
    onSuccess: invalidate,
  });
}

/**
 * «ثبت پرداخت» for a sale that is still owed — the instalment the patient
 * brings in next week.
 *
 * `POST /patient-payments` with `patientPackageId`, the same ledger entry the
 * accounting section writes (a record of money that changed hands at the desk;
 * nothing is charged). Only a receipt LINKED to the sale counts towards its
 * `amountPaidRials` / `balanceRials`, which is why this form always links it.
 *
 * The receipt moves numbers outside this section's keys too: the patient's
 * outstanding balance (`['patients', …]`), the sessions screen's copy of it
 * (`['sessions', …]`) and the accounting ledger (`['accounting', …]`). All are
 * refreshed, so the patient file does not keep offering a debt just paid.
 */
export function useRecordPackagePayment(): UseMutationResult<
  CreatePatientPaymentResponse,
  unknown,
  CreatePatientPaymentDto
> {
  const queryClient = useQueryClient();
  const invalidate = useInvalidatePackages();
  return useMutation({
    mutationFn: (body: CreatePatientPaymentDto) => patientPaymentsCreate(body),
    onSuccess: () => {
      invalidate();
      for (const root of ['patients', 'sessions', 'accounting']) {
        void queryClient.invalidateQueries({ queryKey: [root] });
      }
    },
  });
}

/**
 * ── WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────────
 *
 * `POST /patient-packages/:id/consume` and `/release`. Drawing a session off a
 * package happens when a treatment is recorded, needs the session it is drawn
 * against, and belongs to the sessions screen — which sends `patientPackageId`
 * with the session itself (`useSessionPackages` + `package-choice.tsx`), so the
 * draw and the treatment are one transaction. This feature defines bundles,
 * sells them, and reports what is left — cancelling and refunding included,
 * because those are commercial decisions about a SALE rather than records of a
 * treatment.
 */
